import { EstateType } from "@1games-ru/region-shared/dist/enums/estate/estate";
import { useTranslation } from "react-i18next";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { Exit2Icon, LockIcon, LockOpenIcon, Star2Icon } from "@/shared/icons";
import { Button, Modal } from "@/shared/ui";
import KeyActionPanel, { KeyAction } from "@/shared/ui/KeyActionPanel";
import { estateEnterInfoSelector, setEstateEnterInfo } from "@/domains/estate/slice";
import useEstateEnter from "@/domains/estate/hooks/useEstateEnter";
import useToggleLockEstate from "@/domains/estate/hooks/useToggleLockEstate";
import EstateMainInfo from "@/domains/estate/components/EstateMainInfo";
import EstateView from "@/domains/estate/components/EstateView";
import KeyCode from "@/assets/constants/key-code";

export default function EstateEnterModalContainer({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation("estate");
  const estate = useAppSelector(estateEnterInfoSelector);
  const [enterEstate, enterGarage] = useEstateEnter();
  const { toggleLockEstate } = useToggleLockEstate();
  const dispatch = useAppDispatch();

  const actionsArr: [KeyCode, KeyAction][] = [
    [
      KeyCode.E,
      {
        label: t("estate.enter"),
        keyName: "E",
        action: enterEstate,
        color: "red",
      },
    ],
    [
      KeyCode.ESCAPE,
      {
        label: t("estate.close"),
        keyName: "ESC",
        action: () => {},
        color: "red",
      },
    ],
  ];

  if (estate.type === EstateType.House) {
    actionsArr.splice(1, 0, [
      KeyCode.G,
      {
        label: t("estate.enterGarage"),
        keyName: "G",
        action: enterGarage,
        color: "red",
      },
    ]);
  }

  const actions = new Map<KeyCode, KeyAction>(actionsArr);

  const title = (
    <div className="flex items-center gap-2.5 w-full">
      <div className="text-18 text-white font-bold">
        {t("estate.home")} #{estate.id}
      </div>
      <div className="bg-white flex items-center gap-1 py-1 pl-1 pr-1.5 rounded-[0.25rem] uppercase">
        <Star2Icon size={12} className="text-dark-800" />
        <div className="text-12 text-[#0F0F0F] font-bold leading-0">
          {t(`estate.level.${estate.level}`)}
        </div>
      </div>
    </div>
  );

  const showFooter = estate.isResident || estate.isSale || !estate.isLocked;
  const footer = showFooter ? (
    <div className="grid grid-cols-3 gap-2.5">
      <Button className="flex items-center gap-2.5 h-[2.625rem]" onClick={enterEstate}>
        <Exit2Icon size={16} />
        {t("estate.enterEstate")}
      </Button>
      {estate.type === EstateType.House && (
        <Button
          variant="soft"
          color="gray"
          className="flex items-center gap-2.5 h-[2.625rem]"
          onClick={enterGarage}
        >
          <Exit2Icon size={16} />
          {t("estate.enterGarage")}
        </Button>
      )}
      <Button
        variant="soft"
        color="gray"
        className="flex items-center gap-2.5 h-[2.625rem]"
        onClick={() => {
          toggleLockEstate(estate, () => {
            dispatch(setEstateEnterInfo({ ...estate, isLocked: !estate.isLocked }));
          });
        }}
      >
        {estate.isLocked ? <LockIcon size={16} /> : <LockOpenIcon size={16} />}
        {t(`estate.${!estate.isLocked ? "unlock" : "lock"}`)}
      </Button>
    </div>
  ) : null;

  return (
    <>
      <Modal
        open
        onClose={onClose}
        style={{
          maxHeight: "calc(100vh - 2 * var(--boundary-offset-y) - 6rem)",
        }}
        overlay={false}
        header={<EstateView estate={estate} isResidence={estate.isResident} />}
        footer={footer}
        title={title}
      >
        <EstateMainInfo info={estate} />
      </Modal>
      <KeyActionPanel
        actions={actions}
        name="estate-enter-modal"
        className="fixed bottom-[--boundary-offset-y] left-[50%] -translate-x-[50%] z-[51]"
      />
    </>
  );
}
